/**
 * views/bind.ts —— 槽/字段绑定（U-923 槽解析→绑定；判据 i6_view_fields_visibility_declared）。
 * 纪律：值只来自投影（pointerValues／fact／approval）；缺键保留缺席（不补造值）；
 *      未声明字段 ⇒ 抛（红）；masked 只出脱敏形态（U-930 §3「脱敏」）；internal 不出值。
 */
import type { A2UIField } from "../renderer/a2ui";
import type { ApprovalCard, RenderDesc, ViewAssembly } from "../transport/dto";
import type { FieldDecl, Visibility } from "./schema";
import { E_CODES } from "../transport/errorText";

/** 投影输入（消费面＝S 路 U-922；此处只读） */
export interface ProjectionInput {
  assembly?: ViewAssembly;
  desc?: RenderDesc;
  approval?: ApprovalCard;
  /** 槽值/投影列值（键＝声明 source；不透明，由投影消费面解析供给） */
  pointerValues?: Record<string, string>;
  /** 行式投影（列表/流/图的行） */
  rows?: Array<Record<string, string>>;
  /**
   * **节点行**（★C-Ⅷ）：按声明 `main.flow_row_source` 由闭集载体行还原的 12 类节点行
   * （`{key, kind, createdAt, state}`）。中栏流渲染走它；不在位 ⇒ 渲染层回落 `rows`
   * 里带 `kind` 的行（向后兼容）。
   */
  flowRows?: ReadonlyArray<Record<string, unknown>>;
}

export class BindingViolation extends Error {
  constructor(message: string) {
    super(E_CODES.FIELD_BINDING + "：" + message);
    this.name = "BindingViolation";
  }
}

/** 字段 → 呈现项（可见性按声明；未声明字段由生成器拦截，此处再设防） */
export function bindField(field: FieldDecl, data: ProjectionInput): A2UIField {
  return toA2UIField(field, resolveValue(field, data));
}

/** 行式绑定（表/流/图的行值） */
export function bindRow(field: FieldDecl, row: Record<string, string>): A2UIField {
  const raw = row[field.source ?? field.key] ?? row[field.key];
  return toA2UIField(field, raw);
}

/** 结构化事实 → 行值（缺键不补造；数组/数值转文本，形态确定） */
export function factToRow(fact: Record<string, unknown>): Record<string, string> {
  const row: Record<string, string> = {};
  for (const [key, value] of Object.entries(fact ?? {})) {
    row[key] = toText(value) ?? "";
  }
  return row;
}

/** 按声明渲染值：masked ⇒ 脱敏；空值 ⇒ empty_display（缺省空串，不补造） */
export function toA2UIField(field: FieldDecl, raw: string | undefined): A2UIField {
  const base = raw ?? "";
  const value = present(field, base);
  return { key: field.key, visibility: field.visibility, value };
}

function present(field: FieldDecl, raw: string): string {
  if (raw === "") return field.empty_display ?? "";
  return field.visibility === "masked" ? maskOpaque(raw) : raw;
}

/**
 * 脱敏（不透明账号维）：留头 2＋尾 2，中段遮蔽（确定性，同值同形）。
 * 依据：U-930 §3 actor_account 行「脱敏｜wf.actor.account_ref（不透明）」。
 */
export function maskOpaque(value: string): string {
  if (value.length <= 4) return "…";
  return value.slice(0, 2) + "…" + value.slice(-2);
}

function resolveValue(field: FieldDecl, data: ProjectionInput): string | undefined {
  const candidates: unknown[] = [
    field.source ? data.pointerValues?.[field.source] : undefined,
    data.pointerValues?.[field.key],
    data.assembly?.fact?.[field.key],
    data.approval?.[field.key],
  ];
  for (const candidate of candidates) {
    const text = toText(candidate);
    if (text !== undefined) return text;
  }
  return undefined;
}

function toText(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value) && value.every((v) => typeof v === "string")) {
    return value.join(",");
  }
  return undefined;
}

/** 供生成器用：可见性透传（类型锚） */
export type { Visibility };
